import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { IconButton } from '@flipster/react/design-system/icon-button';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import SettingIcon from '@flipster/react/icon/generated/SettingIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { tradingLeaderboardBunja } from './bunja';
import { tierInfos, tradingLeaderboardTiers } from './shared/constants';

interface HeaderProps {
  css: Styles;
}
export const Header = ({ css: cssProps }: HeaderProps) => {
  const { openProfileSettings, tierDescriptionsAtom } = useBunja(tradingLeaderboardBunja);
  const tierDescriptions = useAtomValue(tierDescriptionsAtom);
  return (
    <div className={css(hstack.raw({ justify: 'space-between', tabletDown: { px: 16 } }), cssProps)}>
      <h1 className={css({ textStyle: 'Display/XL', color: 'inverse.white' })}>
        <FormattedMessage id='Autogen_common.r251126.s_PnL_leaderboard' />
      </h1>
      <div className={hstack({ justify: 'flex-end', gap: 8 })}>
        <IconButton
          hierarchy='quaternary'
          size='medium'
          icon={<InformationCircleIcon />}
          onClick={() => {
            segment.track(TradingLeaderboardEvent.LeaderboardInfoClick);
            alert.show({
              title: 'P&L leaderboard',
              message: (
                <div className={stack({ textStyle: 'Text/L/Regular', gap: 16 })}>
                  <p className={css({ color: 'content.default.level-5' })}>
                    Your league is set each week or month based on your balance. It stays locked until the next round.
                  </p>
                  <div
                    className={stack({
                      justify: 'center',
                      gap: 4,
                      p: 12,
                      rounded: 8,
                      bg: 'background.default.level-3',
                      color: 'content.default.level-5',
                    })}
                  >
                    <p className={css({ textStyle: 'Text/L/Strong' })}>League tiers</p>
                    {tierDescriptions && (
                      <ul className={css({ ml: 24, '&, & > li': { listStyle: 'disc' } })}>
                        {tradingLeaderboardTiers.map(
                          (tier) =>
                            tierDescriptions[tier] && (
                              <li key={tier}>
                                {tierInfos[tier].label}: {tierDescriptions[tier]}
                              </li>
                            ),
                        )}
                      </ul>
                    )}
                  </div>
                  <div className={stack({ gap: 4 })}>
                    <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
                      League balance schedule
                    </p>
                    <ul className={css({ ml: 24, '&, & > li': { listStyle: 'disc' } })}>
                      <li>Weekly: Every Monday</li>
                      <li>Monthly: Every 1st of the month</li>
                    </ul>
                  </div>
                </div>
              ),
              confirmText: 'OK',
            });
          }}
        />
        <IconButton hierarchy='quaternary' size='medium' icon={<SettingIcon />} onClick={openProfileSettings} />
      </div>
    </div>
  );
};
